import React, { useState, useMemo, useCallback, useRef } from "react";
import { ReactComponent as ExercisesIcon } from "../../icons/exerciseIcon.svg";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  Skeleton,
  Typography,
  useMediaQuery,
} from "@mui/material";
import dayjs from "dayjs";
import minMax from "dayjs/plugin/minMax";
dayjs.extend(minMax);
import { Calendar } from "lucide-react";
import ClientSectionCard from "./ClientSectionCard";
import TimeRangeButtons, {
  TimeRangeUnit,
} from "../client-exercise-history/components/TimeRangeButtons";
import { useUserIsClient } from "../../hooks/useCurrentUser";
import WorkoutResultsService from "../../services/WorkoutResultsService";
import { useQuery } from "@tanstack/react-query";
import {
  CLIENT_EXERCISE_HISTORY_ROUTE,
  COACH_CLIENT_EXERCISE_HISTORY_ROUTE,
} from "../../routes/routes";
import { getTimeRange } from "../../utils/date";
import makeStyles from "@mui/styles/makeStyles";
import { findDefaultTimeRange } from "../client-exercise-history/utils";
import BodyPartBalanceRadar from "./BodyPartBalanceRadar";
import { UserExerciseDto } from "@growth-machine-llc/stridist-api-client";
import { ProfileIconBlock } from "../coach-clients/ProfileIconBlock";
import ClientDetailsCardHeader from "../coach-clients/ClientDetailsCardHeader";
import { ArrowForwardRounded } from "@mui/icons-material";
import { JourneyAnchors } from "../../constants";
import clsx from "clsx";

const useStyles = makeStyles((theme) => ({
  root: {
    flex: "1 1 50%",
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(3),
    overflow: "visible",
    containerType: "inline-size",
  },

  timeRangeText: {
    display: "flex",
    alignItems: "center",
    color: theme.palette.text.secondary,
  },
  viewHistoryButton: {
    color: theme.palette.primary.main,
    backgroundColor: "transparent",
    fontSize: 14,
    fontWeight: 600,
    boxShadow: "none",
    height: theme.spacing(4),
    [theme.containerQueries.down("md")]: {
      paddingLeft: 0,
    },
  },
  viewHistoryIcon: {
    color: theme.palette.primary.main,
    fontSize: "20px!important",
  },
}));

const DEFAULT_TIME_RANGE_VIEW = TimeRangeUnit.MONTH;
const MIN_ENTRIES_FOR_DEFAULT_VIEW = 5;

export type ExerciseState = {
  selectedExercise: UserExerciseDto;
};

export const CLIENT_EXERCISES_LIST_QUERY_KEY = "client-exercises-list";

const ClientExercisesSection: React.FC<{
  clientId: number;
  username?: string;
  createdAt?: string;
  isPreview?: boolean;
  isFirst?: boolean;
  collapsable?: boolean;
  defaultOpen?: boolean;
}> = ({
  clientId,
  username,
  createdAt,
  isPreview,
  isFirst,
  collapsable,
  defaultOpen,
}) => {
  const s = useStyles();

  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const isClient = useUserIsClient();

  const [isOpenContent, setIsOpenContent] = React.useState(!!defaultOpen);
  const [activeUnit, setActiveUnit] = useState<TimeRangeUnit>(
    DEFAULT_TIME_RANGE_VIEW,
  );
  const [navIndex, setNavIndex] = useState(0);

  const { start: startDate, end: endDate } = React.useMemo(() => {
    return getTimeRange(activeUnit, navIndex);
  }, [activeUnit, navIndex]);

  const { data: allExercises, isLoading } = useQuery({
    queryKey: [CLIENT_EXERCISES_LIST_QUERY_KEY, { clientId }],
    queryFn: () =>
      WorkoutResultsService.getCompletedExercisesByDateRanges(clientId),
  });

  const exercisesWithBodypartData = useMemo(
    () => allExercises?.filter((ex) => ex.bodyparts.length > 0),
    [allExercises],
  );

  const filteredExercises = useMemo(
    () =>
      exercisesWithBodypartData?.filter(
        ({ completedAt: d }) =>
          (!startDate || !d.isBefore(startDate)) &&
          (!endDate || !d.isAfter(endDate)),
      ),
    [exercisesWithBodypartData, startDate, endDate],
  );

  const minDate = useMemo(
    () =>
      exercisesWithBodypartData?.length
        ? dayjs.min(
            exercisesWithBodypartData.map((r) =>
              dayjs(r.completedAt.toISOString()),
            ),
          )
        : null,
    [exercisesWithBodypartData],
  );

  const maxDate = useMemo(
    () =>
      exercisesWithBodypartData?.length
        ? dayjs.max(
            exercisesWithBodypartData.map((r) =>
              dayjs(r.completedAt.toISOString()),
            ),
          )
        : null,
    [exercisesWithBodypartData],
  );

  // set the nav index to show the most recent date range on first load
  // if there are not enough entries in that range, switch to all time view
  React.useEffect(() => {
    if (!maxDate) {
      return;
    }

    const { unit, index } = findDefaultTimeRange({
      maxDate,
      records: exercisesWithBodypartData.map((ex) => ({
        ...ex,
        completedAt: ex.completedAt.toISOString(),
      })),
      minEntries: MIN_ENTRIES_FOR_DEFAULT_VIEW,
      defaultUnit: DEFAULT_TIME_RANGE_VIEW,
    });

    setActiveUnit(unit);
    setNavIndex(index);
  }, [exercisesWithBodypartData, maxDate]);

  const historyPath = isClient
    ? CLIENT_EXERCISE_HISTORY_ROUTE
    : COACH_CLIENT_EXERCISE_HISTORY_ROUTE.replace(":username", username);

  const handleViewHistory = useCallback(() => {
    navigate(historyPath);
    window.scrollTo({ top: 0 });
  }, [historyPath, navigate]);

  const disablePrev =
    activeUnit === TimeRangeUnit.ALL_TIME ||
    !minDate ||
    startDate?.isSame(minDate, "day") ||
    startDate?.isBefore(minDate, "day");

  const today = dayjs();

  const disableNext =
    activeUnit === TimeRangeUnit.ALL_TIME ||
    !endDate ||
    endDate.isSame(today, "day") ||
    endDate.isAfter(today, "day");

  const highlightDates = useMemo(() => {
    const isAllTime = activeUnit === TimeRangeUnit.ALL_TIME;

    const parsed = createdAt ? dayjs(createdAt) : null;
    const safeCreated = parsed && parsed.isValid() ? parsed : null;

    const safeMinDate = minDate && dayjs(minDate).isValid() ? minDate : null;

    const startAllTimeFallback = safeCreated || safeMinDate || today;

    return {
      start: isAllTime ? startAllTimeFallback : startDate,
      end: isAllTime ? today : endDate,
    };
  }, [activeUnit, createdAt, startDate, endDate, minDate, today]);

  const handleExerciseSelect = React.useCallback(
    (userExercise: UserExerciseDto) => {
      navigate(historyPath, {
        state: {
          selectedExercise: userExercise,
        } as ExerciseState,
      });
    },
    [historyPath, navigate],
  );

  const viewExerciseHistoryButton = (
    <Button
      variant="text"
      className={s.viewHistoryButton}
      endIcon={<ArrowForwardRounded className={s.viewHistoryIcon} />}
      children={`View ${collapsable ? "exercise" : ""} history`}
      onClick={handleViewHistory}
      disabled={isPreview}
    />
  );

  return (
    <Card
      ref={containerRef}
      className={clsx(s.root, JourneyAnchors.CLIENT_EXERCISES)}
      elevation={0}
      variant="outlined"
    >
      <ClientDetailsCardHeader
        title="Exercises"
        icon={<ProfileIconBlock icon={<ExercisesIcon />} bgVariant="accent" />}
        subtitle={
          <Box display="flex" alignItems="flex-start" flexDirection={"column"}>
            {!isLoading ? (
              <Box className={s.timeRangeText}>
                <Calendar size={16} />
                <Typography
                  variant="body2"
                  color="textSecondary"
                  paddingLeft={1}
                >
                  {highlightDates.start?.format("MMM D, YYYY")} -{" "}
                  {highlightDates.end?.format("MMM D, YYYY")}
                </Typography>
              </Box>
            ) : (
              <Skeleton width={150} height={20} />
            )}
          </Box>
        }
        children={!collapsable && viewExerciseHistoryButton}
        isOpenContent={isOpenContent}
        setIsOpenContent={setIsOpenContent}
        isFirst={isFirst}
        collapsable={collapsable}
        defaultOpen={defaultOpen}
        isPreview={isPreview}
      />

      {isOpenContent && (
        <>
          <Box sx={{ mb: 2 }}>
            <TimeRangeButtons
              activeButton={activeUnit}
              setActiveButton={setActiveUnit}
              navIndex={navIndex}
              setNavIndex={setNavIndex}
              disablePrev={disablePrev}
              disableNext={disableNext}
              separateArrows
            />
          </Box>

          <BodyPartBalanceRadar
            exercises={filteredExercises}
            onExerciseSelect={handleExerciseSelect}
            isLoading={isLoading}
          />
        </>
      )}
    </Card>
  );
};

export default React.memo(ClientExercisesSection);
